import "../../stylesheets2025/finalPaper.css";
import ScrollToTop from "../../components2026/ScrollToTop";

/* ===================== CONFIG ===================== */
const CMT_SUBMISSION_URL = "https://cmt3.research.microsoft.com/SANKET2026";
const PDF_EXPRESS_CONFERENCE_ID = "72753X";
const CAMERA_READY_DEADLINE = "10th November 2026";

const FinalPaperSubmission = () => {
    return (
        <div className="containAll fps-page flex flex-col">
            <ScrollToTop />

            <section className="fps-hero">
                <span className="fps-kicker">For Authors · SANKET 2026</span>
                <h1>Final Paper Submission</h1>
                <p>Camera-Ready Paper Guidelines</p>
                <span className="fps-pill">4 – 5 December 2026 · IIT Bombay</span>
            </section>

            <div className="fps-shell">
                <div className="fps-deadline">
                    <span className="dot" aria-hidden="true" />
                    <div>
                        <div><strong>Camera-Ready Paper Submission:</strong> {CAMERA_READY_DEADLINE}</div>
                        <div><strong>Early Bird Registration:</strong> 7th November 2026</div>
                        <div><strong>Final Registration Deadline:</strong> 10th November 2026</div>
                    </div>
                </div>

                {/* 1. Paper Requirements */}
                <section className="fps-section">
                    <h2><span className="fps-num">1</span> Paper Requirements</h2>
                    <ul className="fps-list">
                        <li>The final paper must be prepared in the <strong>IEEE two-column format</strong> and submitted as a <strong>PDF</strong>. Page limit is <strong>6 pages (A4)</strong> and the file size must not exceed <strong>3 MB</strong>.</li>
                        <li>Up to <strong>2 additional pages</strong> are permitted on payment of the additional page charges (see Registration). Papers must not exceed <strong>8 pages</strong> in any case.</li>
                        <li>IEEE manuscript templates are available at{" "}
                            <a href="https://www.ieee.org/conferences/publishing/templates.html" target="_blank" rel="noopener noreferrer">ieee.org/conferences/publishing/templates</a>.
                        </li>
                        <li>Authors are solely responsible for ensuring the final manuscript is free from plagiarism before it is uploaded for IEEE Xplore. See{" "}
                            <a href="https://www.ieee.org/publications/rights/plagiarism/plagiarism.html" target="_blank" rel="noopener noreferrer">IEEE plagiarism policy</a>.
                        </li>
                        <li><strong>Plagiarism must be less than 20% and AI content must be less than 10%.</strong> Any violation will lead to deletion of the paper from the conference proceedings.</li>
                        <li>The <strong>title, author names and author order</strong> must be identical to the accepted submission. No changes are permitted in the final version.</li>
                        <li>A paper will be included in the conference proceedings and submitted to IEEE Xplore only if <strong>at least one author completes full registration</strong> and <strong>presents the paper</strong> at the conference.</li>
                        <li>Authors must carefully address all reviewers' comments in the camera-ready version.</li>
                        <li>No IEEE copyright notice / footer number needs to be added to the paper for SANKET 2026.</li>
                    </ul>
                </section>

                {/* 2. PDF eXpress */}
                <section className="fps-section">
                    <h2><span className="fps-num">2</span> Check the Final Paper in IEEE PDF eXpress</h2>
                    <p className="fps-lead">Before uploading, every author must first check the final PDF on the IEEE PDF eXpress verification portal to make sure the format is IEEE Xplore-compatible.</p>
                    <ul className="fps-list">
                        <li><strong>Before creating the PDF:</strong> proofread the source document thoroughly. No revisions should be needed after this point.</li>
                        <li>Log in to the IEEE PDF eXpress site:{" "}
                            <a href="https://ieee-pdf-express.org/account/login" target="_blank" rel="noopener noreferrer">ieee-pdf-express.org</a>
                        </li>
                        <li><strong>First-time users:</strong> create an account, then enter <strong className="fps-code">{PDF_EXPRESS_CONFERENCE_ID}</strong> as the Conference ID, your email address and a password. Continue as prompted — a confirmation will be displayed and emailed to you.</li>
                        <li><strong>Previous users:</strong> follow the same steps using your existing password, and verify that your contact information is current.</li>
                        <li>Rename the final PDF as <strong className="fps-code">PID-&lt;Paper ID&gt;.pdf</strong>, using your Paper ID in CMT (for example, <em>PID-0136.pdf</em>).</li>
                        <li>Upload the PDF, wait for the check to complete, and download the <strong>PDF eXpress-approved</strong> version.</li>
                        <li><strong>Upload only the IEEE PDF eXpress-verified PDF file for the final camera-ready submission.</strong> Any change made to the file after verification (headers, page numbers, watermarks, etc.) may invalidate IEEE Xplore compatibility.</li>
                        <li>Support: <a href="mailto:PDFSupport@ieee.org">PDFSupport@ieee.org</a></li>
                    </ul>
                </section>

                {/* 3. Copyright */}
                <section className="fps-section">
                    <h2><span className="fps-num">3</span> Submit the IEEE Copyright Form</h2>
                    <ul className="fps-list">
                        <li>Log in to the CMT Author Console and verify all paper details (title, authors, order) before submitting the form.</li>
                        <li>Upload the final paper (the PDF eXpress-approved version) and click the <strong>"Submit IEEE Copyright Form"</strong> link in the Author Console.</li>
                        <li>Read the instructions on the IEEE Copyright Form Submission page.</li>
                        <li>Complete the form on the IEEE Copyright website. A PDF copy can be downloaded at the end and is also emailed to the corresponding author. Save a copy; you will then be redirected back to the Author Console.</li>
                        <li>Upload the downloaded copyright form in the designated area (drag-and-drop or <em>Upload from Computer</em>) and click <strong>Save</strong>.</li>
                        <li>Detailed CMT help:{" "}
                            <a href="https://cmt3.research.microsoft.com/docs/help/author/camera-ready-submission.html#ieee-copyright" target="_blank" rel="noopener noreferrer">CMT – Camera-ready submission &amp; IEEE copyright</a>
                        </li>
                    </ul>
                </section>

                {/* 4. Checklist */}
                <section className="fps-section fps-checklist">
                    <h2><span className="fps-num">4</span> Final Submission Checklist</h2>
                    <ul className="fps-list">
                        <li>Reviewers' comments addressed</li>
                        <li>IEEE two-column format, ≤ 8 pages, ≤ 3 MB</li>
                        <li>Title / authors / order unchanged</li>
                        <li>PDF eXpress check passed (file named PID-&lt;Paper ID&gt;.pdf)</li>
                        <li>IEEE Copyright Form submitted and uploaded in CMT</li>
                        <li>At least one author registered (see <a href="/2026/registration">Registration</a>)</li>
                    </ul>
                </section>

                <div className="fps-actions">
                    <a className="fps-btn primary" href={CMT_SUBMISSION_URL} target="_blank" rel="noopener noreferrer">
                        Submit Camera-Ready Paper (CMT) ↗
                    </a>
                    <a className="fps-btn secondary" href="https://ieee-pdf-express.org/account/login" target="_blank" rel="noopener noreferrer">
                        IEEE PDF eXpress ↗
                    </a>
                </div>

                <p className="fps-contact">
                    For queries: <a href="mailto:sanket@ecovillage.org.in">sanket@ecovillage.org.in</a>
                </p>
            </div>
        </div>
    );
};

export default FinalPaperSubmission;


// import "../../stylesheets2025/finalPaper.css";
// import ScrollToTop from "../../components2026/ScrollToTop";

// /* =====================================================================
//    CONFIG — 
//    ===================================================================== */
// const CMT_SUBMISSION_URL = "https://cmt3.research.microsoft.com/SANKET2026";   // TODO: confirm exact CMT link
// const PDF_EXPRESS_CONFERENCE_ID = "XXXXXX";                                   // TODO: SANKET 2026 IEEE PDF eXpress Conference ID
// const CAMERA_READY_DEADLINE = "TBA";                                          // TODO: e.g. "15th November 2026"

// const FinalPaperSubmission = () => {
//     return (
//         <div className="containAll fps-page flex flex-col">
//             <ScrollToTop />

//             <section className="fps-hero">
//                 <span className="fps-kicker">For Authors · SANKET 2026</span>
//                 <h1>Final Paper Submission</h1>
//                 <p>Camera-Ready Paper Guidelines</p>
//                 <span className="fps-pill">4 – 5 December 2026 · IIT Bombay</span>
//             </section>

//             <div className="fps-shell">
//                 <div className="fps-deadline">
//                     <span className="dot" aria-hidden="true" />
//                     <div>
//                         <strong>Camera-Ready Submission Deadline:</strong> {CAMERA_READY_DEADLINE}
//                     </div>
//                 </div>

//                 {/* 1. Paper Requirements */}
//                 <section className="fps-section">
//                     <h2><span className="fps-num">1</span> Paper Requirements</h2>
//                     <ul className="fps-list">
//                         <li>The final paper must be prepared in the <strong>IEEE two-column format</strong> and submitted as a <strong>PDF</strong>. Page limit is <strong>6 pages (A4)</strong> and the file size must not exceed <strong>3 MB</strong>.</li>
//                         <li>Up to <strong>2 additional pages</strong> are permitted on payment of the additional page charges (see Registration). Papers must not exceed <strong>8 pages</strong> in any case.</li>
//                         <li>IEEE manuscript templates are available at{" "}
//                             <a href="https://www.ieee.org/conferences/publishing/templates.html" target="_blank" rel="noopener noreferrer">ieee.org/conferences/publishing/templates</a>.
//                         </li>
//                         <li>Authors are solely responsible for ensuring the final manuscript is free from plagiarism before it is uploaded for IEEE Xplore. See{" "}
//                             <a href="https://www.ieee.org/publications/rights/plagiarism/plagiarism.html" target="_blank" rel="noopener noreferrer">IEEE plagiarism policy</a>.
//                         </li>
//                         <li>The <strong>title, author names and author order</strong> must be identical to the accepted submission. No changes are permitted in the final version.</li>
//                         <li>A paper will be included in the conference proceedings and submitted to IEEE Xplore only if <strong>at least one author completes full registration</strong> and <strong>presents the paper</strong> at the conference.</li>
//                         <li>Authors must carefully address all reviewers' comments in the camera-ready version.</li>
//                         <li>No IEEE copyright notice / footer number needs to be added to the paper for SANKET 2026.</li>
//                     </ul>
//                 </section>

//                 {/* 2. PDF eXpress */}
//                 <section className="fps-section">
//                     <h2><span className="fps-num">2</span> Check the Final Paper in IEEE PDF eXpress</h2>
//                     <ol className="fps-steps">
//                         <li>Log in to IEEE PDF eXpress:{" "}
//                             <a href="https://ieee-pdf-express.org/account/login" target="_blank" rel="noopener noreferrer">ieee-pdf-express.org</a>
//                         </li>
//                         <li><strong>First-time users:</strong> click <em>New Users</em>, enter the Conference ID <strong className="fps-code">{PDF_EXPRESS_CONFERENCE_ID}</strong>, your email address and a password, and follow the prompts. You will receive an on-screen confirmation and an email verifying your account.</li>
//                         <li><strong>Returning users:</strong> log in with the password from your previous conferences, confirm your contact details and use Conference ID <strong className="fps-code">{PDF_EXPRESS_CONFERENCE_ID}</strong>.</li>
//                         <li>Before uploading, rename the final PDF as <strong className="fps-code">PID-XXXX.pdf</strong>, where XXXX is your Paper ID in CMT (for example, <em>PID-0136.pdf</em>).</li>
//                         <li>Upload the PDF, wait for the check to complete, and download the <strong>PDF eXpress-approved</strong> version. Only this approved PDF should be submitted in CMT.</li>
//                     </ol>
//                 </section>
//                 {/* 3. Copyright */}
//                 <section className="fps-section">
//                     <h2><span className="fps-num">3</span> Submit the IEEE Copyright Form</h2>
//                     <ol className="fps-steps">
//                         <li>Log in to the CMT Author Console and verify all paper details (title, authors, order) before submitting the form.</li>
//                         <li>Upload the final paper (the PDF eXpress-approved version) and click the <strong>"Submit IEEE Copyright Form"</strong> link in the Author Console.</li>
//                         <li>Read the instructions on the IEEE Copyright Form Submission page.</li>
//                         <li>Complete the form on the IEEE Copyright website. A PDF copy can be downloaded at the end and is also emailed to the corresponding author. Save a copy; you will then be redirected back to the Author Console.</li>
//                         <li>Upload the downloaded copyright form in the designated area (drag-and-drop or <em>Upload from Computer</em>) and click <strong>Save</strong>.</li>
//                         <li>Detailed CMT help:{" "}
//                             <a href="https://cmt3.research.microsoft.com/docs/help/author/camera-ready-submission.html#ieee-copyright" target="_blank" rel="noopener noreferrer">CMT – Camera-ready submission &amp; IEEE copyright</a>
//                         </li>
//                     </ol>
//                 </section>

//                 {/* 4. Checklist */}
//                 <section className="fps-section fps-checklist">
//                     <h2><span className="fps-num">4</span> Final Submission Checklist</h2>
//                     <ul className="fps-list">
//                         <li>Reviewers' comments addressed</li>
//                         <li>IEEE two-column format, ≤ 8 pages, ≤ 3 MB</li>
//                         <li>Title / authors / order unchanged</li>
//                         <li>PDF eXpress check passed (file named PID-XXXX.pdf)</li>
//                         <li>IEEE Copyright Form submitted and uploaded in CMT</li>
//                         <li>At least one author registered (see <a href="/2026/registration">Registration</a>)</li>
//                     </ul>
//                 </section>

//                 <div className="fps-actions">
//                     <a className="fps-btn primary" href={CMT_SUBMISSION_URL} target="_blank" rel="noopener noreferrer">
//                         Submit Camera-Ready Paper (CMT) ↗
//                     </a>
//                     <a className="fps-btn secondary" href="https://ieee-pdf-express.org/account/login" target="_blank" rel="noopener noreferrer">
//                         IEEE PDF eXpress ↗
//                     </a>
//                 </div>

//                 <p className="fps-contact">
//                     For queries: <a href="mailto:sanket@ecovillage.org.in">sanket@ecovillage.org.in</a>
//                 </p>
//             </div>
//         </div>
//     );
// };

// export default FinalPaperSubmission;
